import type { CityConfig } from "../city-template/types";

export const delawareConfig: CityConfig = {
  city: "Delaware",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "delaware-oh",
  route: "/cities/delaware-oh",
  title: "Emergency Passport Delaware, OH | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Delaware, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Delaware County and north Columbus suburbs. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Delaware, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Delaware and north Columbus area travelers — including Powell, Lewis Center, and Sunbury — get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Delaware%20Ohio%20downtown%20cityscape%20with%20charming%20historic%20main%20street%20and%20Delaware%20County%20courthouse%2C%20beautiful%20small%20town%20America%20with%20brick%20buildings%20and%20tree-lined%20streets%2C%20warm%20afternoon%20sunlight%20on%20well-preserved%20architecture%2C%20professional%20urban%20photography%20with%20warm%20earth%20tones%20and%20a%20peaceful%20atmosphere%2C%20northwest%20Columbus%20suburb%20setting&width=1800&height=700&seq=delaware-oh-hero-001&orientation=landscape",
  heroImageAlt: "Delaware Ohio historic downtown with courthouse and main street",
  geoPosition: "40.2987;-83.0680",
  geoPlacename: "Delaware, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 280 miles from Delaware via I-70",
  },
  nearbyCitiesZips: [
    { name: "Delaware", zips: "43015" },
    { name: "Powell", zips: "43065" },
    { name: "Lewis Center", zips: "43035" },
    { name: "Sunbury", zips: "43074" },
    { name: "Westerville", zips: "43081, 43082" },
    { name: "Dublin", zips: "43016, 43017" },
    { name: "Worthington", zips: "43085" },
    { name: "Marysville", zips: "43040" },
    { name: "Plain City", zips: "43064" },
    { name: "Marion", zips: "43301–43307" },
    { name: "Mount Gilead", zips: "43338" },
    { name: "Bellefontaine", zips: "43311" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Delaware departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-global-line", title: "Delaware County Coverage", desc: "Serving Delaware County and north Columbus suburbs" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Columbus, OH", route: "/cities/columbus-oh" },
    { name: "Fast passport renewal in Dublin, OH", route: "/cities/dublin-oh" },
    { name: "Emergency passport help in Marion, OH", route: "/cities/marion-oh" },
    { name: "Urgent passport services in Newark, OH", route: "/cities/newark-oh" },
    { name: "Same-day passport help in Lancaster, OH", route: "/cities/lancaster-oh" },
    { name: "Passport courier services in Lima, OH", route: "/cities/lima-oh" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Delaware remotely via secure courier. We do not have a walk-in office in Delaware — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Delaware County and north Columbus area travelers.",
  fpcOfficeDistance: "Serving all of Delaware County and north Columbus suburbs",
  serviceAreaIntro: "From downtown Delaware to Powell and Lewis Center, from Sunbury to Westerville and Dublin — Fast Passport Center serves the entire Delaware County and north Columbus region. We regularly help travelers from Worthington, Marysville, and Plain City get their passports fast.",
  nearbyCitiesIntro: "We serve all Delaware neighborhoods and the surrounding Delaware County and north Columbus region including Powell, Lewis Center, Sunbury, Westerville, Dublin, Worthington, Marysville, Plain City, Marion, Mount Gilead, and Bellefontaine. If you are anywhere in the Delaware area or north Columbus suburbs, we have you covered.",
  howItWorksIntro: "Most Delaware travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Delaware travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Delaware and Delaware County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Delaware? Here are the most common ones we hear from Delaware County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Delaware international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Andrew W.",
      location: "Delaware, OH",
      rating: 5,
      text: "My passport expired three weeks before my business trip to London. Fast Passport Center had my renewal ready in 5 days. They were professional, thorough, and kept me informed.",
      service: "Passport Renewal",
    },
    {
      name: "Nicole S.",
      location: "Powell, OH",
      rating: 5,
      text: "Emergency family trip to Ireland required a passport in 48 hours. Fast Passport Center delivered in 36 hours. I was amazed at how smooth the process was.",
      service: "Emergency Passport",
    },
    {
      name: "James D.",
      location: "Lewis Center, OH",
      rating: 5,
      text: "First passport for my teenage daughter's school trip to France. They walked us through every requirement and had her passport in 6 days. Couldn't ask for better service.",
      service: "Child Passport",
    },
    {
      name: "Linda F.",
      location: "Westerville, OH",
      rating: 5,
      text: "Lost my passport before my Caribbean cruise. Fast Passport Center handled the replacement process and got it to me in 4 days. They saved our family vacation.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "delaware-1",
      name: "Delaware Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "35 S Liberty St",
      city: "Delaware",
      state: "OH",
      zip: "43015",
      phone: "(740) 363-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Delaware",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main downtown facility. High volume — appointments strongly recommended. Call ahead for availability.",
      icon: "ri-mail-line",
    },
    {
      id: "delaware-2",
      name: "Powell Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 W Olentangy St",
      city: "Powell",
      state: "OH",
      zip: "43065",
      phone: "(614) 764-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Powell, 5 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Powell and Lewis Center residents. Less crowded than downtown. Call for appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "delaware-3",
      name: "Westerville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "617 S State St",
      city: "Westerville",
      state: "OH",
      zip: "43081",
      phone: "(614) 882-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Westerville, 10 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Westerville and northeast Franklin County residents. Ample parking. Call ahead for appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "delaware-4",
      name: "Marysville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "201 E 5th St",
      city: "Marysville",
      state: "OH",
      zip: "43040",
      phone: "(937) 642-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Union County, 15 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for northwest Delaware and Marysville area residents. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "delaware-5",
      name: "Delaware County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "91 N Sandusky St",
      city: "Delaware",
      state: "OH",
      zip: "43015",
      phone: "(740) 833-2500",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Delaware County Courthouse",
      website: "https://www.co.delaware.oh.us/",
      websiteLabel: "Delaware County Courts",
      notes: "Accepts passport applications. Good alternative to post office with extended hours and appointment availability.",
      icon: "ri-government-line",
    },
    {
      id: "delaware-6",
      name: "Dublin Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "6400 Emerald Pkwy",
      city: "Dublin",
      state: "OH",
      zip: "43016",
      phone: "(614) 761-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Dublin, 15 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Dublin and northwest Franklin County residents. Call for appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Delaware photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Delaware County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Delaware applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Delaware County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Delaware travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Delaware." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Delaware address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Delaware before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Delaware for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Delaware?", a: "Fast Passport Center offers fast passport renewal in Delaware in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "Can I get a same-day passport appointment near Delaware?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — approximately 280 miles from Delaware via I-70 — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Delaware County residents." },
    { q: "Can I expedite a child passport in Delaware?", a: "Yes. Fast Passport Center expedites child passports in Delaware for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Delaware County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Delaware with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "What is the closest passport agency to Delaware?", a: "The nearest regional passport agency to Delaware is the Chicago Passport Agency, approximately 280 miles away via I-70. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Delaware without any in-person agency visit required." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Can you help travelers from Powell and Westerville?", a: "Absolutely. Fast Passport Center serves all of Delaware County and the surrounding north Columbus region including Powell, Lewis Center, Sunbury, Westerville, Dublin, Worthington, Marysville, Plain City, Marion, Mount Gilead, and Bellefontaine. Our courier service works seamlessly from any zip code in the area." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Delaware", address: "1760 Columbus Pike, Delaware, OH 43015", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Delaware", address: "150 E William St, Delaware, OH 43015", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart Supercenter — Delaware", address: "1760 Columbus Pike, Delaware, OH 43015", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Delaware", address: "100 S Liberty St, Delaware, OH 43015", hours: "Mon–Sun: 9AM–9PM" },
    { name: "CVS Pharmacy — Powell", address: "50 W Olentangy St, Powell, OH 43065", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Westerville", address: "500 S State St, Westerville, OH 43081", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Marysville", address: "1000 Coleman Dr, Marysville, OH 43040", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Dublin", address: "6400 Perimeter Loop Rd, Dublin, OH 43017", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "Delaware BMV — Delaware County",
      address: "1005 Pittsburgh Dr, Delaware, OH 43015",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Powell BMV — Delaware County",
      address: "50 W Olentangy St, Powell, OH 43065",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Delaware County driver's license and REAL ID services. Alternative for south Delaware residents.",
      featured: false,
    },
    {
      name: "Westerville BMV — Franklin County",
      address: "500 S State St, Westerville, OH 43081",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Franklin County BMV. Alternative for southeast Delaware and Westerville area residents.",
      featured: false,
    },
    {
      name: "Marysville BMV — Union County",
      address: "1000 Coleman Dr, Marysville, OH 43040",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Union County BMV. Alternative for northwest Delaware and Marysville area residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "25 miles south of Delaware",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Nearest major commercial airport with direct flights to major hubs. Primary airport for Delaware County international travelers.",
      flightUrl: "https://www.flycmh.com/",
      flightLabel: "Columbus Airport",
      featured: true,
    },
    {
      code: "LCK",
      name: "Rickenbacker International Airport",
      distance: "35 miles south of Delaware",
      address: "2241 John Circle Dr, Columbus, OH 43217",
      note: "Regional airport serving central Ohio with cargo operations and limited passenger services. Alternative for south Delaware.",
      flightUrl: "https://www.rickenbacker.org/",
      flightLabel: "Rickenbacker Airport",
      featured: false,
    },
    {
      code: "DAY",
      name: "Dayton International Airport",
      distance: "75 miles west of Delaware",
      address: "3600 Terminal Dr, Dayton, OH 45377",
      note: "Alternative airport for west Delaware. Additional flight options and sometimes easier parking.",
      flightUrl: "https://flydayton.com/",
      flightLabel: "Dayton Airport",
      featured: false,
    },
  ],
};